import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, X, ArrowRight, Rocket, ShoppingCart, KeyRound, Clock, ExternalLink, Sparkles, Music, Mic, Image as ImageIcon, Video } from "lucide-react";
import { useCurrency } from "@/lib/currency";

/**
 * Global "out of credits" modal. Listens for four events fired by the
 * axios interceptor:
 *   - bfa:credits-low          → new credit-wallet 402
 *   - bfa:ai-cap-reached       → legacy generation-count 402 (kept for back-compat)
 *   - bfa:trial-blocked        → trial expired / grace period
 *   - bfa:ai-provider-capped   → Update #97 BYOK free-tier exceeded
 */

const BYOK_CATEGORY_ICONS = {
  voice: Mic,
  music: Music,
  image: ImageIcon,
  video: Video,
};

const BYOK_CATEGORY_LABELS = {
  voice: "voice generations",
  music: "music renders",
  image: "image fetches",
  video: "video renders",
};

export default function CapReachedModal() {
  const { fmt: fx } = useCurrency();
  const [data, setData] = useState(null);
  const [legacy, setLegacy] = useState(false);
  const [kind, setKind] = useState("credits");

  useEffect(() => {
    const onCredits = (e) => { setLegacy(false); setData(e.detail); setKind("credits"); };
    const onLegacy = (e) => { setLegacy(true); setData(e.detail); setKind("credits"); };
    const onTrial = (e) => { setLegacy(false); setData(e.detail); setKind("trial"); };
    const onByok = (e) => { setLegacy(false); setData(e.detail); setKind("byok"); };
    window.addEventListener("bfa:credits-low", onCredits);
    window.addEventListener("bfa:ai-cap-reached", onLegacy);
    window.addEventListener("bfa:trial-blocked", onTrial);
    window.addEventListener("bfa:ai-provider-capped", onByok);
    return () => {
      window.removeEventListener("bfa:credits-low", onCredits);
      window.removeEventListener("bfa:ai-cap-reached", onLegacy);
      window.removeEventListener("bfa:trial-blocked", onTrial);
      window.removeEventListener("bfa:ai-provider-capped", onByok);
    };
  }, []);

  if (!data) return null;

  // BYOK Free-Tier Capped → dedicated modal (Update #97)
  if (kind === "byok") {
    const providerLabel = data.provider_label || data.provider || "this provider";
    const Icon = BYOK_CATEGORY_ICONS[data.category] || Sparkles;
    const categoryLabel = BYOK_CATEGORY_LABELS[data.category] || "generations";
    const used = Number(data.used || 0);
    const limit = Number(data.limit || 0);
    const reason = data.reason || (used >= limit && limit > 0 ? "monthly_limit_reached" : "platform_key_missing");
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-up px-4"
        data-testid="byok-cap-modal"
        onClick={() => setData(null)}
      >
        <div
          className="relative w-full max-w-md rounded-2xl border border-amber-500/40 bg-bg-surface p-6 shadow-2xl shadow-amber-500/10"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="absolute right-3 top-3 text-ink-muted hover:text-ink-primary"
            onClick={() => setData(null)}
            data-testid="byok-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-amber-500/15 text-amber-400 grid place-items-center">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-400">
                // {reason === "monthly_limit_reached" ? "FREE TIER REACHED" : "KEY NEEDED"}
              </div>
              <div className="font-display text-xl font-bold">
                {reason === "monthly_limit_reached"
                  ? `Free ${providerLabel} tier reached`
                  : `${providerLabel} key needed`}
              </div>
            </div>
          </div>

          {limit > 0 && (
            <div className="rounded-xl border border-line bg-bg-elevated p-3 mb-4">
              <div className="flex items-center justify-between text-xs text-ink-muted mb-1.5">
                <span>This month</span>
                <span data-testid="byok-modal-usage-text">{used} / {limit} {categoryLabel}</span>
              </div>
              <div className="h-2 rounded-full bg-bg-base overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-rose-400 transition-all"
                  style={{ width: `${Math.min(100, limit ? (used / limit) * 100 : 0)}%` }}
                />
              </div>
            </div>
          )}

          <p className="text-sm text-ink-secondary mb-5 leading-relaxed">
            {data.message ||
              `You've hit the free-tier limit for ${providerLabel}. Connect your own ${providerLabel} API key for unlimited usage, or wait until the free tier resets.`}
          </p>

          <div className="space-y-2 mb-3">
            <Link
              to="/dashboard/ai-providers"
              onClick={() => setData(null)}
              data-testid="byok-modal-connect-key"
              className="flex items-center justify-between gap-3 rounded-lg border border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20 p-3 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <KeyRound className="h-4 w-4 text-amber-400 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold">Connect your own key</div>
                  <div className="text-xs text-ink-muted">Pay your own provider — no free-tier limits</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-ink-muted group-hover:text-amber-400 transition-colors" />
            </Link>

            <div className="flex items-center gap-3 rounded-lg border border-line bg-bg-elevated/40 p-3">
              <Clock className="h-4 w-4 text-ink-muted flex-shrink-0" />
              <div className="text-xs text-ink-muted leading-relaxed">
                Free tier resets on the 1st of every month (UTC).
                {data.key_help_url && (
                  <>
                    {" "}
                    <a
                      href={data.key_help_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-400 hover:underline inline-flex items-center gap-0.5"
                      data-testid="byok-modal-key-help"
                    >
                      Get a {providerLabel} key
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Trial-blocked → render a dedicated trial-expired modal
  if (kind === "trial") {
    const isGrace = data.error === "trial_grace";
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-up px-4"
        data-testid="trial-blocked-modal"
        onClick={() => setData(null)}
      >
        <div
          className="relative w-full max-w-md rounded-2xl border border-rose-500/40 bg-bg-surface p-6 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="absolute right-3 top-3 text-ink-muted hover:text-ink-primary"
            onClick={() => setData(null)}
            data-testid="trial-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-xl bg-rose-500/15 text-rose-300 grid place-items-center">
              <Coins className="h-5 w-5" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300">
                // {isGrace ? "TRIAL ENDED · GRACE PERIOD" : "TRIAL EXPIRED"}
              </div>
              <div className="font-display text-xl font-bold">
                {isGrace ? "Your free trial has ended." : "Free trial expired."}
              </div>
            </div>
          </div>
          <p className="text-sm text-ink-secondary mb-5">
            {data.message || "Upgrade now to keep generating new content. Your existing content, stations, and integrations remain intact."}
          </p>
          <div className="flex gap-2">
            <Link
              to="/dashboard/billing?tab=plan"
              onClick={() => setData(null)}
              data-testid="trial-modal-cta"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-4 py-2.5 text-sm font-semibold text-black amber-glow"
            >
              Upgrade now <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              onClick={() => setData(null)}
              className="rounded-md border border-line bg-bg-elevated px-4 py-2.5 text-sm text-ink-secondary hover:text-ink-primary transition"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Adapt both shapes to a common view-model
  const vm = legacy
    ? {
        plan: data.current_plan,
        eyebrow: "MONTHLY CAP REACHED",
        title: "You hit your AI generation limit.",
        usage: `${data.used} / ${data.cap}`,
        message: data.message,
        cta: { to: "/dashboard/billing?tab=plan", label: data.suggest ? `Upgrade to ${data.suggest.name}` : "View plans" },
        suggest: data.suggest,
        showTopups: false,
      }
    : {
        plan: data.plan,
        eyebrow: "OUT OF CREDITS",
        title: `${humanFeature(data.feature)} needs ${data.required} credit${data.required > 1 ? "s" : ""}.`,
        usage: `${data.available} available`,
        message: `You only have ${data.available} credits left on the ${data.plan} plan. Top up below or upgrade your plan to keep generating.`,
        cta: { to: "/dashboard/billing?tab=credits", label: "Top up credits" },
        suggest: null,
        showTopups: true,
        packs: data.topup_packs || [],
      };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-up px-4"
      data-testid="credits-modal"
      onClick={() => setData(null)}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-brand-amber/40 bg-bg-surface p-6 shadow-2xl shadow-brand-amber/10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute right-3 top-3 text-ink-muted hover:text-ink-primary"
          onClick={() => setData(null)}
          data-testid="credits-modal-close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-xl bg-brand-amber/15 text-brand-amber grid place-items-center">
            <Coins className="h-5 w-5" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-amber">// {vm.eyebrow}</div>
            <div className="font-display text-xl font-bold">{vm.title}</div>
          </div>
        </div>

        <div className="rounded-xl border border-line bg-bg-elevated p-4 mb-4">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-xs font-mono uppercase tracking-wider text-ink-muted capitalize">
              {String(vm.plan || "free").replace("_", " ")} plan
            </span>
            <span className="font-display text-2xl font-black text-rose-400" data-testid="credits-modal-usage">
              {vm.usage}
            </span>
          </div>
          <p className="mt-3 text-xs text-ink-secondary">{vm.message}</p>
        </div>

        {vm.showTopups && vm.packs?.length > 0 && (
          <div className="rounded-xl border border-brand-amber/40 bg-brand-amber/5 p-3 mb-5">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingCart className="h-3.5 w-3.5 text-brand-amber" />
              <span className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">// TOP-UP PACKS</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {vm.packs.slice(0, 4).map((p) => (
                <div key={p.sku} className="rounded border border-line bg-bg-surface p-2 text-xs">
                  <div className="font-display font-bold text-brand-amber">{p.credits.toLocaleString()} cr</div>
                  <div className="text-ink-secondary">{fx(p.price_usd)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {vm.suggest && (
          <div className="rounded-xl border border-brand-amber/40 bg-brand-amber/5 p-4 mb-5">
            <div className="flex items-center gap-2 mb-1">
              <Rocket className="h-4 w-4 text-brand-amber" />
              <span className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">// SUGGESTED UPGRADE</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="font-display text-lg font-bold">{vm.suggest.name}</span>
              <span className="font-display text-2xl font-black">${vm.suggest.price}<span className="text-xs text-ink-muted font-normal">/mo</span></span>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Link
            to={vm.cta.to}
            onClick={() => setData(null)}
            data-testid="credits-modal-cta"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-brand-amber px-4 py-2.5 text-sm font-semibold text-black amber-glow"
          >
            {vm.cta.label} <ArrowRight className="h-4 w-4" />
          </Link>
          <button
            onClick={() => setData(null)}
            data-testid="credits-modal-dismiss"
            className="rounded-md border border-line bg-bg-elevated px-4 py-2.5 text-sm text-ink-secondary hover:text-ink-primary transition"
          >
            Maybe later
          </button>
        </div>

        <div className="mt-3 text-center text-[10px] font-mono uppercase tracking-wider text-ink-muted">
          Monthly credits reset on the 1st
        </div>
      </div>
    </div>
  );
}

function humanFeature(key) {
  const m = {
    generate_bio: "Artist bio",
    generate_caption: "Social caption",
    generate_social_post: "Social post",
    generate_hashtags: "Hashtags",
    generate_dj_script: "AI DJ script",
    generate_news_bulletin: "News bulletin",
    generate_podcast: "Podcast meta",
    talk_show_to_podcast: "Podcast notes",
    generate_campaign_pack: "Campaign pack",
    translate_content: "Translation",
    generate_commercial: "Commercial",
    imaging_generate: "Image generation",
    blog_post: "Blog post",
    ai_default: "This AI feature",
  };
  return m[key] || "This AI feature";
}
